import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import PaginationComp from "./Pagination";
import { useEffect, useState, useMemo } from "react";
import * as Icons from "@/assets/features/candidates";
import PropTypes from "prop-types";
import Filters from "./Filter";
import EmptyScreening from "../shared/EmptyScreening";
import BulkActionsBar from "../shared/BulkActionsBar";

export default function DataTable({
  columns = [],
  data = [],
  onTableReady,
  onSelectionChange,
  selectedRows=[],
  rowSelection = {},
  setRowSelection,
  showCheckbox = true,
  loading = false,
  lengthShow = 5,
  // =======
  showPagination = false,
  count,
  hasNext,
  paginationAfterId,
  paginationBeforeId,
  // =========
  title,
  actions,
  enableExport,
  enableSort,
  enableSearch,
  enableColumns,
  enableFilter,
  actionsBulk,
  FILTERS,
  modalKeys,
  view,
  activeModal,
  setActiveModal,
  queryKey,
  searchKey,
  disabledCheckboxes,
  belowToolbar,
  emptyState,
}) {
  const [sorting, setSorting] = useState([]);

  
  const initialColumnVisibility = useMemo(() => {
    const visibility = {};
    columns?.forEach((col, index) => {
      const columnId = col.id || col.accessorKey;
      if (!columnId) return;

      if (col.enableHiding === false) {
        visibility[columnId] = true;
      } else {
        visibility[columnId] = index < lengthShow;
      }
    });
    return visibility;
  }, [columns, lengthShow]);

  const [columnVisibility, setColumnVisibility] = useState(
    initialColumnVisibility,
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...(showCheckbox && { rowSelection }),
      columnVisibility,
    },
    onSortingChange: setSorting,
    ...(showCheckbox && { onRowSelectionChange: setRowSelection }),
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    
    
  });

  useEffect(() => {
    if (onTableReady) onTableReady(table);
  }, [table, onTableReady, disabledCheckboxes]);

  useEffect(() => {
    if (!showCheckbox || !onSelectionChange) return;

    const selected = table
      .getSelectedRowModel()
      .rows?.map((row) => row.original);

    onSelectionChange(selected);
  }, [rowSelection, showCheckbox, onSelectionChange, table]);

  
  
  return (
    <>
      <Filters
        table={table}
        count={count}
        title={title}
        actions={actions}
        enableExport={enableExport}
        enableSort={enableSort}
        enableFilter={enableFilter}
        enableSearch={enableSearch}
        enableColumns={enableColumns}
        FILTERS={FILTERS}
        modalKeys={modalKeys}
        view={view}
        activeModal={activeModal}
        setActiveModal={setActiveModal}
        queryKey={queryKey}
        searchKey={searchKey}
        selectedRows={selectedRows}
        setRowSelection={setRowSelection}
        onSelectionChange={onSelectionChange}
        belowToolbar={belowToolbar}
      />
      <div
        className={` rounded-[8px] text-md flex flex-col ${showPagination ? "mb-0" : "mb-10"}`}
      >
        <div className="min-h-0 overflow-x-auto !rounded-[8px] scroll-x">
          <Table>
            <TableHeader className="bg-primary-foreground h-[3.5rem] capitalize">
              {table.getHeaderGroups()?.map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {showCheckbox && (
                    <TableHead>
                      <Checkbox
                        className="mx-2"
                        checked={table.getIsAllPageRowsSelected()}
                        onCheckedChange={(checked) =>
                        {
                          table.toggleAllPageRowsSelected(!!checked)
                        }
                        }
                          disabled={disabledCheckboxes?.length}

                        
                      />
                    </TableHead>
                  )}

                  {headerGroup.headers?.map((header) => (
                    <TableHead
                      key={header.id}
                      className="cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}

                        {header.column.getCanSort() && (
                          <Icons.Sort className="h-3 w-3" />
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody className="text-secondary">
              {loading ? (
                [...Array(7)]?.map((_, i) => (
                  <TableRow key={i} className="animate-pulse">
                    {showCheckbox && (
                      <TableCell>
                        <div className="h-4 w-4 bg-gray-300 rounded" />
                      </TableCell>
                    )}

                    {columns?.slice(0, lengthShow)?.map((_, idx) => (
                      <TableCell key={idx}>
                        <div className="h-4 w-[70%] bg-gray-300 rounded" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows?.map((row) => (
                  <TableRow key={row.id}>
                    {showCheckbox && (
                      <TableCell>
                        <Checkbox
                          className={`mx-2`}
                          checked={row.getIsSelected()}
                          onCheckedChange={(checked) =>{
                            row.toggleSelected(!!checked)
                          }
                          }
                          disabled={disabledCheckboxes?.includes(row.original.id)}
                        />
                      </TableCell>
                    )}

                    {row.getVisibleCells()?.map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={(columns?.length || 0) + (showCheckbox ? 1 : 0)}
                    className="py-6 align-top"
                  >
                    {emptyState ?? (
                      <div className="text-center">
                        <EmptyScreening title="Not Data Available" />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

      </div>
      <div className=" w-auto shrink-0  pt-4 relative top-10  ">
        {showPagination && (
            <PaginationComp
              className="mt-0 absolute !mb-20"
              afterId={
                paginationAfterId ??
                table.getRowModel().rows?.[table.getRowModel().rows.length - 1]
                  ?.original?.id
              }
              beforeId={
                paginationBeforeId ??
                table.getRowModel().rows?.[0]?.original?.id
              }
              items={table.getRowModel().rows}
              count={count}
              hasNext={hasNext}
            />
          )}
          </div>
      {selectedRows.length&&actionsBulk ? (
        <BulkActionsBar
          actions={actionsBulk}
          selectedRows={selectedRows}
          setSelectedRows={(rows) => {
            onSelectionChange(rows);
            if (!rows.length) setRowSelection({});
          }}
        />
      ):(<></>)}
    </>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  onTableReady: PropTypes.func,
  onSelectionChange: PropTypes.func,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func,
  showCheckbox: PropTypes.bool,
  loading: PropTypes.bool,
  lengthShow: PropTypes.number,
  showPagination: PropTypes.bool,
  count: PropTypes.number,
  hasNext: PropTypes.bool,
  paginationAfterId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  paginationBeforeId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  emptyState: PropTypes.node,
};
